import { DETALHES_ANATOMIA } from './detalhesAnatomia'
import type { AcaoPeca } from './estadoPecas'
import type { ClasseAco } from './classesAco'
import { rotuloLadoVeiculo } from './rotulosLado'
import { COR_POR_CATEGORIA, siglaVisivel, type CategoriaDidatica } from './tokens'

/**
 * Conteúdo pedagógico das peças, separado da geometria.
 *
 * Nenhum mesh deve carregar texto didático hardcoded: o instrutor precisa
 * poder revisar nome e descrição sem remodelar o veículo.
 */
export interface ComponenteDidatico {
  id: string
  nome: string
  categoria: CategoriaDidatica
  grupo: 'estrutura' | 'carroceria' | 'habitaculo' | 'seguranca' | 'mecanica' | 'energia'
  descricao: string
  /** finalidade estrutural, mecânica ou de segurança didaticamente revisada */
  funcao?: string
  /** leitura operacional adicional, sem afirmar composição universal */
  material?: string
  /**
   * Classe de aço provável **desta maquete**, não do gênero do veículo.
   *
   * Alimenta a camada de aços e, adiante, o módulo de técnicas de corte. Toda
   * peça que declara classe também declara `confirmarNaFicha`: a maquete
   * descreve uma construção plausível, e a ocorrência se resolve na ficha de
   * salvamento do veículo.
   */
  classeAco?: ClasseAco
  atencaoOperacional?: string
  confirmarNaFicha?: boolean
  /** sigla curta para rótulo 3D; só existe quando já validada */
  sigla?: string
  corSemantica: string
  acoes: AcaoPeca[]
}

type Entrada = Omit<ComponenteDidatico, 'corSemantica' | 'acoes'> & {
  corSemantica?: string
  acoes?: AcaoPeca[]
}

const ACOES_PADRAO: AcaoPeca[] = ['destacar', 'transparencia', 'ocultar', 'restaurar']
const ACOES_PORTA: AcaoPeca[] = ['destacar', 'abrir', 'afastar', 'remover', 'restaurar']
const ACOES_PAINEL: AcaoPeca[] = ['destacar', 'transparencia', 'afastar', 'ocultar', 'restaurar']
const ACOES_SOMENTE_DESTAQUE: AcaoPeca[] = ['destacar', 'restaurar']

function normalizar(entrada: Entrada): ComponenteDidatico {
  return {
    ...entrada,
    corSemantica: entrada.corSemantica ?? COR_POR_CATEGORIA[entrada.categoria],
    acoes: entrada.acoes ?? ACOES_PADRAO,
  }
}

const LADOS = [
  { sufixo: 'Esquerda', chave: 'esquerdo' },
  { sufixo: 'Direita', chave: 'direito' },
] as const

/** Colunas A/B/C nos dois lados; a coluna B é segmentada (estratégia de corte). */
function entradasColunas(): Entrada[] {
  const saida: Entrada[] = []
  for (const lado of LADOS) {
    saida.push({
      id: `colunaA${lado.sufixo}`,
      nome: `Coluna A do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      sigla: siglaVisivel('colunaA') ?? undefined,
      descricao:
        'Montante dianteiro que emoldura o para-brisa. Faz parte da gaiola do habitáculo e sustenta a extremidade dianteira do teto.',
      funcao:
        'Sustentar o arco dianteiro do teto, delimitar o vão do para-brisa e transferir esforços da dianteira para a gaiola do habitáculo em colisões frontais ou capotamentos.',
      material: 'Pode combinar aços de diferentes resistências e espessuras.',
      classeAco: 'ultraAlta',
      atencaoOperacional: 'Reconheça reforços e dispositivos próximos antes de definir corte ou afastamento.',
      confirmarNaFicha: true,
    })
    saida.push({
      id: `colunaB${lado.sufixo}`,
      nome: `Coluna B do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      sigla: siglaVisivel('colunaB') ?? undefined,
      descricao:
        'Montante central entre as portas. É modelada em três segmentos para permitir demonstrar, visualmente, uma remoção pré-definida sem cortar malha em tempo real.',
      funcao:
        'Preservar o espaço de sobrevivência dos ocupantes em impactos laterais e capotamentos, servindo também de apoio para as travas das portas dianteiras e dobradiças das traseiras.',
      material: 'Frequentemente reúne várias camadas e aços de alta ou ultra-alta resistência.',
      classeAco: 'boro',
      atencaoOperacional: 'A resistência e os componentes internos podem mudar entre gerações do mesmo veículo.',
      confirmarNaFicha: true,
      acoes: ['destacar', 'transparencia', 'ocultar', 'restaurar'],
    })
    for (const segmento of ['Superior', 'Central', 'Inferior'] as const) {
      saida.push({
        id: `colunaB${lado.sufixo}.segmento${segmento}`,
        nome: `Coluna B do ${rotuloLadoVeiculo(lado.chave)} — segmento ${segmento.toLowerCase()}`,
        categoria: 'estrutura',
        grupo: 'estrutura',
        descricao:
          'Segmento pré-definido da coluna B. A maquete não faz corte arbitrário: ela oculta ou afasta segmentos já modelados.',
        funcao:
          'Segmento da coluna central para estudo de técnicas de alívio e corte progressivo com ferramentas hidráulicas de desencarceramento.',
        acoes: ['destacar', 'afastar', 'remover', 'restaurar'],
      })
    }
    saida.push({
      id: `colunaC${lado.sufixo}`,
      nome: `Coluna C do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      sigla: siglaVisivel('colunaC') ?? undefined,
      descricao:
        'Montante traseiro do habitáculo. No hatch acompanha a inclinação acentuada do vidro traseiro.',
      funcao:
        'Sustentar a porção posterior do teto e fechar a gaiola de sobrevivência sobre o compartimento de bagagens e ocupantes traseiros.',
      material: 'Composição e reforços variam conforme a carroceria e o projeto.',
      classeAco: 'alta',
      confirmarNaFicha: true,
    })
    saida.push({
      id: `soleira${lado.sufixo}`,
      nome: `Soleira do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Viga longitudinal na base da lateral, entre as caixas de roda. Um dos caminhos principais de carga da estrutura.',
      funcao:
        'Resistir a esforços longitudinais e de flexão na base da carroceria, ancorando as colunas e distribuindo a carga de impacto pelo assoalho.',
      material: 'Seção formada por chapas e reforços sobrepostos, com resistência variável.',
      classeAco: 'alta',
      atencaoOperacional: 'A aparência externa não revela quantas camadas existem.',
      confirmarNaFicha: true,
    })
    saida.push({
      id: `longarina${lado.sufixo}`,
      nome: `Longarina do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Estrutura longitudinal simplificada sob o piso, ligando as zonas de absorção dianteira e traseira ao habitáculo.',
      funcao:
        'Absorver e dissipar energia cinética na colisão frontal através de deformação programada, direcionando a carga residual para o habitáculo.',
      material: 'Elemento estrutural cuja composição e seção dependem do projeto do veículo.',
      classeAco: 'alta',
      confirmarNaFicha: true,
    })
  }
  return saida
}

/** Subpeças de cada porta: é o que o aluno vê ao afastar a chapa externa. */
function entradasPorta(id: string, nome: string): Entrada[] {
  return [
    {
      id,
      nome,
      categoria: 'carroceria',
      grupo: 'carroceria',
      descricao:
        'Peça independente. Pode ser aberta, ter a chapa externa afastada para revelar o interior, ou ser removida por completo.',
      funcao:
        'Permitir acesso aos ocupantes e complementar a proteção lateral através de barras de reforço e travamento nas colunas.',
      acoes: ACOES_PORTA,
    },
    {
      id: `${id}.chapaExterna`,
      nome: `${nome} — chapa externa`,
      categoria: 'carroceria',
      grupo: 'carroceria',
      descricao:
        'Chapa externa da porta, com a maçaneta. Afastá-la expõe a barra de reforço, a máquina do vidro, as dobradiças e o limitador.',
      funcao:
        'Garantir aerodinâmica, acabamento e vedação externa, servindo de barreira superficial antes dos componentes estruturais da porta.',
      acoes: ACOES_PAINEL,
    },
    {
      id: `${id}.vidro`,
      nome: `${nome} — vidro`,
      categoria: 'carroceria',
      grupo: 'carroceria',
      descricao: 'Vidro da porta, representado como superfície translúcida.',
      funcao:
        'Proporcionar visibilidade e isolamento da cabine; temperado fragmenta-se em pequenos cubos para reduzir o risco de laceração.',
      material: 'Normalmente temperado, mas pode ser laminado, policarbonato ou integrar um conjunto blindado.',
      atencaoOperacional: 'Identifique a marcação e a espessura aparente antes de escolher a técnica.',
      confirmarNaFicha: true,
    },
    {
      id: `${id}.barraReforco`,
      nome: `${nome} — barra de reforço lateral`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Barra interna da porta, representada de forma simplificada. Aparece ao afastar a chapa externa.',
      funcao:
        'Dissipar a força de impactos laterais diretamente para as colunas A e B, minimizando a intrusão na altura do tórax e abdômen dos ocupantes.',
      material: 'Pode usar aço de alta resistência e geometrias diferentes conforme o modelo.',
      classeAco: 'ultraAlta',
      atencaoOperacional: 'Sua posição influencia onde a porta oferece maior resistência.',
      confirmarNaFicha: true,
    },
    {
      id: `${id}.dobradicaSuperior`,
      nome: `${nome} — dobradiça superior`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao: 'Fixação superior da porta na coluna dianteira do vão.',
      funcao:
        'Fixar e articular a porta na coluna, mantendo a integridade do vão em impactos e servindo de ponto de apoio ou alívio no corte.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.dobradicaInferior`,
      nome: `${nome} — dobradiça inferior`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao: 'Fixação inferior da porta na coluna dianteira do vão.',
      funcao:
        'Fixar e articular a base da porta na coluna, suportando o peso e as cargas dinâmicas da porta.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.limitador`,
      nome: `${nome} — limitador`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Braço que limita a abertura da porta, ligando-a à coluna entre as duas dobradiças. Corre por um guia e termina em batente. Esquemático: o curso é representado, não reproduzido em escala.',
      funcao:
        'Restringir o curso angular de abertura da porta para não atingir a carroceria ou para-lamas adjacentes.',
      atencaoOperacional:
        'Resiste junto com as dobradiças quando a porta é forçada além do curso: conte com ele ao planejar afastamento ou remoção.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.fechadura`,
      nome: `${nome} — região de fechadura/trava`,
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao: 'Região de travamento na borda traseira da porta.',
      funcao:
        'Reter a porta travada contra o pino da coluna B/C, evitando a ejeção dos ocupantes em colisões e capotamentos.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.fiacao`,
      nome: `${nome} — fiação`,
      categoria: 'energia',
      grupo: 'energia',
      descricao:
        'Chicote elétrico que atravessa o vão e mantém a porta ligada à carroceria mesmo depois da liberação dos elementos mecânicos.',
      funcao:
        'Conduzir sinais elétricos e alimentação para travas, vidros elétricos, alto-falantes e sensores da porta.',
      atencaoOperacional:
        'Considere a fiação como vínculo independente antes de movimentar ou receber a porta liberada.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.forro`,
      nome: `${nome} — forro`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Painel de acabamento interno, com puxador e apoio de braço. É o que o ocupante encosta; removê-lo é o gesto que dá acesso ao miolo da porta.',
      funcao:
        'Isolamento acústico e térmico interno com apoio de braço e comandos acessíveis ao ocupante.',
      material: 'Painel leve, preso por presilhas; solta com esforço muito menor que a chapa.',
      acoes: ACOES_PORTA,
    },
    {
      id: `${id}.maquinaVidro`,
      nome: `${nome} — máquina do vidro`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Trilho e braço que sobem e descem o vidro. Ocupam o miolo da porta junto com a barra de reforço, e continuam presos ao vidro quando ele desce.',
      funcao:
        'Mecanismo de elevação e descida do vidro lateral.',
      atencaoOperacional:
        'Baixar o vidro o põe inteiro dentro da porta: considere isso antes de cortar a chapa na altura da cintura.',
      confirmarNaFicha: true,
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.motorVidro`,
      nome: `${nome} — motor do vidro`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Motor elétrico que aciona a máquina do vidro, na base da porta. Depende da alimentação do veículo para funcionar.',
      funcao:
        'Atuador elétrico de movimentação do vidro lateral.',
      atencaoOperacional:
        'Desligada a bateria, o vidro elétrico deixa de descer: decida a ordem entre baixar vidros e cortar a energia.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
    {
      id: `${id}.altoFalante`,
      nome: `${nome} — alto-falante`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Alto-falante alojado na parte baixa da porta, atrás do forro. Ocupa espaço no miolo junto com a máquina do vidro.',
      funcao:
        'Transmissão sonora no sistema de áudio/comunicação do veículo.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    },
  ]
}

function entradasSeguranca(): Entrada[] {
  const saida: Entrada[] = []
  // uma entrada por posição de ocupante: o perfil didático decide quais
  // aparecem, mas o conteúdo existe para todas
  const ocupantes = [
    { sufixo: 'Motorista', nome: 'motorista' },
    { sufixo: 'Passageiro', nome: 'passageiro dianteiro' },
    { sufixo: 'TraseiroEsquerdo', nome: `ocupante traseiro do ${rotuloLadoVeiculo('esquerdo')}` },
    { sufixo: 'TraseiroCentral', nome: 'ocupante traseiro central' },
    { sufixo: 'TraseiroDireito', nome: `ocupante traseiro do ${rotuloLadoVeiculo('direito')}` },
  ]
  for (const o of ocupantes) {
    const central = o.sufixo === 'TraseiroCentral'
    saida.push({
      id: `cinto${o.sufixo}`,
      nome: `Cinto do ${o.nome}`,
      categoria: 'retencao',
      grupo: 'seguranca',
      descricao: central
        ? 'Conjunto de retenção da posição central. Quando é subabdominal, retém a pelve mas não o tronco — o que muda a leitura do movimento do ocupante naquele lugar. O tipo depende do perfil didático selecionado.'
        : 'Conjunto de retenção: faixa diagonal, faixa pélvica, fecho e pontos de ancoragem. É o que mantém o ocupante acoplado ao veículo durante a desaceleração.',
      funcao:
        'Manter o ocupante acoplado ao assento, controlando seu movimento e reduzindo o risco de contato com o interior; não garante ausência de lesão.',
    })
    saida.push({
      id: `pretensionador${o.sufixo}`,
      nome: `Pretensionador do ${o.nome}`,
      categoria: 'retencao',
      grupo: 'seguranca',
      sigla: siglaVisivel('pretensionador') ?? undefined,
      descricao:
        'Dispositivo que recolhe a folga do cinto no início do evento. Representado de forma simbólica junto à ancoragem inferior. Sua presença é configuração do perfil didático — não é universal, e em bancos traseiros é bem menos comum.',
      funcao:
        'Recolher ativamente a folga da fita do cinto nos primeiros milissegundos do impacto, melhorando o acoplamento do corpo ao banco.',
    })
    saida.push({
      id: `ancoragens${o.sufixo}`,
      nome: `Pontos de ancoragem — ${o.nome}`,
      categoria: 'retencao',
      grupo: 'seguranca',
      descricao: 'Pontos de fixação do cinto na estrutura do veículo.',
      funcao:
        'Fixar solidamente as extremidades do cinto à estrutura rígida da carroceria.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    })
  }

  saida.push({
    id: 'centralSrs',
    nome: 'Módulo / Central eletrônica do SRS',
    categoria: 'srs',
    grupo: 'seguranca',
    sigla: siglaVisivel('srs') ?? undefined,
    descricao:
      'Unidade de controle que gerencia os airbags e pré-tensionadores. Pode conter sensores inerciais e reserva de energia após o desligamento; o tempo de espera deve ser consultado no guia do fabricante.',
    funcao:
      'Processar desacelerações registradas por sensores inerciais e ordenar o acionamento em milissegundos dos dispositivos pirotécnicos de segurança.',
    atencaoOperacional:
      'Nunca perfure, corte ou aqueça a região da central ou chicotes amarelos do sistema SRS.',
    acoes: ACOES_SOMENTE_DESTAQUE,
  })
  for (const lado of LADOS) {
    saida.push({
      id: `sensorImpactoDianteiro${lado.sufixo}`,
      nome: `Sensor de impacto dianteiro do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'srs',
      grupo: 'seguranca',
      descricao:
        'Sensor de desaceleração frontal montado na estrutura dianteira. Envia sinal para detonação dos airbags frontais e pré-tensionadores.',
      funcao:
        'Detectar pulsos severos de desaceleração na dianteira para alimentar a lógica de deflagração dos airbags frontais.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    })
    saida.push({
      id: `sensorImpactoLateral${lado.sufixo}`,
      nome: `Sensor de impacto lateral do ${rotuloLadoVeiculo(lado.chave)}`,
      categoria: 'srs',
      grupo: 'seguranca',
      descricao:
        'Sensor de aceleração lateral montado na coluna B ou soleira para detecção de colisão lateral.',
      funcao:
        'Detectar picos de aceleração ou pressão lateral para disparo ultra-rápido de airbags laterais e de cortina.',
      acoes: ACOES_SOMENTE_DESTAQUE,
    })
  }

  const airbags: Array<[string, string, string, string]> = [
    ['airbagMotorista', 'Airbag frontal do motorista', 'Alojado no volante. Só é exibido quando o perfil didático selecionado o inclui.', 'Amortecer a desaceleração da cabeça e tórax do motorista contra o cubo do volante.'],
    ['airbagPassageiro', 'Airbag frontal do passageiro', 'Alojado no painel, lado do passageiro.', 'Amortecer a projeção frontal da cabeça e tórax do passageiro contra o painel do veículo.'],
    ['airbagLateralEsquerdo', `Airbag lateral do ${rotuloLadoVeiculo('esquerdo')}`, 'Protege a região do tórax no impacto lateral.', `Proteger o tórax e pelve do ocupante contra a intrusão da porta no impacto lateral do ${rotuloLadoVeiculo('esquerdo')}.`],
    ['airbagLateralDireito', `Airbag lateral do ${rotuloLadoVeiculo('direito')}`, 'Protege a região do tórax no impacto lateral.', `Proteger o tórax e pelve do ocupante contra a intrusão da porta no impacto lateral do ${rotuloLadoVeiculo('direito')}.`],
    ['airbagCortinaEsquerdo', `Airbag de cortina do ${rotuloLadoVeiculo('esquerdo')}`, 'Desce ao longo da lateral do teto, cobrindo as janelas.', `Proteger a cabeça dos ocupantes do ${rotuloLadoVeiculo('esquerdo')} contra vidros, colunas e objetos externos na colisão lateral ou capotamento.`],
    ['airbagCortinaDireito', `Airbag de cortina do ${rotuloLadoVeiculo('direito')}`, 'Desce ao longo da lateral do teto, cobrindo as janelas.', `Proteger a cabeça dos ocupantes do ${rotuloLadoVeiculo('direito')} contra vidros, colunas e objetos externos na colisão lateral ou capotamento.`],
    ['airbagBancoMotorista', 'Airbag de banco (motorista)', 'Integrado à estrutura do banco. Não existe em todo veículo.', 'Criar uma barreira protetora direta entre a lateral do corpo do ocupante e o painel interno da porta.'],
    ['airbagColunaMotorista', 'Airbag de coluna (motorista)', 'Dispositivo adicional que só aparece quando o perfil didático o solicita.', 'Amortecer o impacto angular da cabeça contra o montante da coluna estrutural.'],
  ]
  for (const [id, nome, descricao, funcao] of airbags) {
    saida.push({
      id,
      nome,
      categoria: 'srs',
      grupo: 'seguranca',
      sigla: siglaVisivel('srs') ?? undefined,
      descricao: `${descricao} A maquete é configurável: a presença e a posição dependem do perfil didático, não de uma regra universal.`,
      funcao,
    })
  }
  return saida
}

/**
 * Subpeças do banco de uma posição de ocupante.
 *
 * O banco não é um bloco: encosto e apoio de cabeça atrapalham a retirada de
 * formas diferentes, e o aluno precisa poder removê-los ou recliná-los e ver a
 * folga da rota mudar.
 */
function entradasBanco(sufixo: string, nome: string): Entrada[] {
  const id = `banco${sufixo}`
  return [
    {
      id,
      nome: `Banco do ${nome}`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Conjunto do banco. Define a âncora lógica do ocupante e, na retirada, é um dos obstáculos reais da rota.',
      acoes: ['destacar', 'transparencia', 'ocultar', 'restaurar'],
    },
    {
      id: `${id}.assento`,
      nome: `Banco do ${nome} — assento`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Base do banco. Recuar no trilho é uma das primeiras medidas para abrir espaço antes da retirada.',
      acoes: ['destacar', 'afastar', 'restaurar'],
    },
    {
      id: `${id}.encosto`,
      nome: `Banco do ${nome} — encosto`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Espaldar do banco. Reclinar reduz a altura que o corpo precisa vencer e muda a folga da rota de retirada.',
      acoes: ['destacar', 'reclinar', 'remover', 'restaurar'],
    },
    {
      id: `${id}.apoioCabeca`,
      nome: `Banco do ${nome} — apoio de cabeça`,
      categoria: 'habitaculo',
      grupo: 'habitaculo',
      descricao:
        'Apoio de cabeça. Fica exatamente atrás da cabeça do ocupante e é um obstáculo frequente na retirada; costuma ser retirado antes da manobra.',
      acoes: ['destacar', 'remover', 'restaurar'],
    },
  ]
}

/**
 * Membros do monobloco fora do habitáculo.
 *
 * A nomenclatura é a de identificação veicular usada no Brasil, para que o
 * aluno encontre na maquete o mesmo nome que ouve na aula e lê no laudo.
 *
 * Como em todo o catálogo, nenhuma descrição afirma composição universal: o que
 * vale para um modelo pode não valer para a geração seguinte do mesmo carro.
 */
function entradasMonobloco(): Entrada[] {
  const porLado: Array<Omit<Entrada, 'id' | 'nome'> & { base: string; nome: string }> = [
    {
      base: 'torreSuspensao',
      nome: 'Torre do amortecedor',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Apoio do conjunto de suspensão dianteira no monobloco. Concentra carga vinda da roda e costuma reunir chapas sobrepostas.',
      material: 'Região de reforço; espessura e número de camadas variam conforme o projeto.',
      atencaoOperacional:
        'É ponto rígido no cofre do motor: avalie antes de escolher apoio de estabilização ou ponto de tracionamento.',
      classeAco: 'alta',
      confirmarNaFicha: true,
    },
    {
      base: 'longarinaDianteira',
      nome: 'Longarina dianteira',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Viga longitudinal que conduz a carga do impacto dianteiro até o corta-fogo e o assoalho. É o caminho principal de força na colisão frontal.',
      material: 'Seção e resistência dependem do projeto; pode reunir mais de uma chapa.',
      atencaoOperacional:
        'Deformação na longarina indica energia que já passou da zona de absorção. Consulte a ficha antes de cortar ou tracionar.',
      classeAco: 'alta',
      confirmarNaFicha: true,
    },
    {
      base: 'prolongadorLongarina',
      nome: 'Prolongador de longarina',
      categoria: 'zonaDeformacao',
      grupo: 'estrutura',
      descricao:
        'Trecho à frente da longarina, dentro da zona de absorção, projetado para colapsar antes dela. É o que costuma ser substituído após impacto leve.',
      material: 'Elemento sacrificial; pode ser aparafusado em vez de soldado.',
      classeAco: 'media',
      confirmarNaFicha: true,
    },
    {
      base: 'avental',
      nome: 'Avental',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Chapa lateral do cofre do motor, entre a torre do amortecedor e a extremidade dianteira. Fecha a caixa de roda por dentro.',
      material: 'Chapa de fechamento; costuma ser menos resistente que longarina e torre.',
      classeAco: 'estampagemProfunda',
      confirmarNaFicha: true,
    },
    {
      base: 'caixaRodaDianteira',
      nome: 'Caixa de roda dianteira',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Concha que envolve a roda dianteira por dentro e separa o cofre do motor da passagem do pneu.',
      material: 'Chapa conformada; costuma ser fina, mas pode ter reforço junto à torre.',
      classeAco: 'estampagemProfunda',
      confirmarNaFicha: true,
    },
    {
      base: 'longarinaTraseira',
      nome: 'Longarina traseira',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Viga longitudinal do assoalho traseiro, que conduz a carga do impacto traseiro. Corre próxima ao tanque de combustível.',
      material: 'Seção e resistência variam conforme o projeto.',
      atencaoOperacional:
        'Antes de cortar a traseira, localize tanque, linhas e, quando houver, cilindro de gás.',
      classeAco: 'alta',
      confirmarNaFicha: true,
    },
    {
      base: 'caixaRodaTraseira',
      nome: 'Caixa de roda traseira',
      categoria: 'estrutura',
      grupo: 'estrutura',
      descricao:
        'Concha que envolve a roda traseira e delimita a largura útil do porta-malas.',
      material: 'Chapa conformada, normalmente sem reforço estrutural pesado.',
      classeAco: 'estampagemProfunda',
      confirmarNaFicha: true,
    },
  ]

  return porLado.flatMap(({ base, nome, ...resto }) =>
    LADOS.map((lado) => ({
      ...resto,
      id: `${base}${lado.sufixo}`,
      nome: `${nome} do ${rotuloLadoVeiculo(lado.chave)}`,
    })),
  )
}

/** Reforços do teto, agora um id por travessa e por longarina. */
function entradasReforcosTeto(): Entrada[] {
  const comuns = {
    categoria: 'estrutura' as const,
    grupo: 'estrutura' as const,
    material: 'Perfil de chapa; a resistência varia conforme o projeto do teto.',
    classeAco: 'media' as const,
    confirmarNaFicha: true,
  }
  return [
    {
      ...comuns,
      id: 'reforcosTeto.travessaDianteira',
      nome: 'Travessa dianteira do teto',
      descricao:
        'Travessa sobre o topo das colunas A, no limite dianteiro do teto. Emoldura a borda superior do para-brisa.',
      funcao:
        'Unir o topo das duas colunas A, sustentando a borda superior do para-brisa e preservando a largura dianteira da cabine.',
    },
    {
      ...comuns,
      id: 'reforcosTeto.travessaCentral',
      nome: 'Travessa central do teto',
      descricao:
        'Travessa transversal na altura das colunas B, no meio do teto. Divide o painel em dois vãos.',
      funcao:
        'Vincular transversalmente o topo das colunas B, impedindo o esmagamento lateral ou colapso do teto sobre os ocupantes dianteiros.',
    },
    {
      ...comuns,
      id: 'reforcosTeto.travessaTraseira',
      nome: 'Travessa traseira do teto',
      descricao:
        'Travessa sobre o topo das colunas C, no limite traseiro do teto. Acompanha a borda do vidro traseiro.',
      funcao:
        'Unir o topo das colunas C, sustentando a moldura do vidro traseiro e fechando o arco estrutural superior da traseira.',
    },
    ...LADOS.map((lado) => ({
      ...comuns,
      id: `reforcosTeto.longarina${lado.sufixo}`,
      nome: `Longarina do teto do ${rotuloLadoVeiculo(lado.chave)}`,
      descricao:
        'Perfil longitudinal na quina entre o teto e a lateral, ligando o topo das colunas A, B e C do mesmo lado.',
      funcao:
        'Formar o arco longitudinal superior da gaiola, resistindo a esforços de flexão e cargas verticais de capotamento.',
    })),
  ]
}

const ENTRADAS: Entrada[] = [
  ...entradasColunas(),
  {
    id: 'travessaPainel',
    nome: 'Travessa/barra de proteção do painel',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Travessa transversal atrás do painel, ligando as laterais do habitáculo. Aparece ao tornar o painel transparente.',
    funcao:
      'Impedir o colapso transversal do habitáculo, escorar as colunas A e sustentar a coluna de direção e conjunto de pedais.',
    classeAco: 'ultraAlta',
    confirmarNaFicha: true,
  },
  {
    id: 'reforcosTeto',
    nome: 'Reforços do teto',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Travessas transversais e longarinas do teto. Ficam visíveis quando o painel de teto é tornado transparente ou removido.',
    funcao:
      'Compor o arco superior de sustentação da gaiola de sobrevivência para evitar o esmagamento dos ocupantes em capotamento.',
  },
  ...entradasReforcosTeto(),
  ...entradasMonobloco(),
  // ---------- conjunto motriz ----------
  {
    id: 'motor',
    nome: 'Motor',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Conjunto do motor no cofre dianteiro. É massa rígida: não se deforma junto com a chapa e desloca-se como bloco no impacto.',
    material: 'Bloco e cabeçote em liga metálica, sobre coxins que podem romper no impacto.',
    atencaoOperacional:
      'Recuo do motor empurra o corta-fogo sobre a pedaleira: verifique aprisionamento de pés antes de movimentar o veículo.',
    acoes: ACOES_PAINEL,
  },
  {
    id: 'motor.bloco',
    nome: 'Bloco do motor',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Corpo principal do motor, onde ficam os cilindros. É a maior massa concentrada do cofre.',
  },
  {
    id: 'motor.cabecote',
    nome: 'Cabeçote',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Peça superior do motor, que fecha os cilindros e aloja o comando de válvulas.',
  },
  {
    id: 'motor.coletorAdmissao',
    nome: 'Coletor de admissão',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Distribui o ar que vem da caixa do filtro para os cilindros. Fica na face do motor voltada ao habitáculo.',
  },
  {
    id: 'motor.coxim',
    nome: 'Coxins do motor',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Apoios que prendem o motor ao berço e ao monobloco. Quando rompem, o motor deixa de estar preso onde o desenho supõe.',
    atencaoOperacional: 'Motor solto do coxim pode se deslocar durante estabilização ou tracionamento.',
    confirmarNaFicha: true,
  },
  {
    id: 'cambio',
    nome: 'Transmissão',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Caixa de transmissão acoplada ao motor. Em tração dianteira fica ao lado dele, atravessada, e entrega força aos semi-eixos.',
    material: 'Carcaça em liga metálica; também é massa rígida.',
    acoes: ACOES_PAINEL,
  },
  {
    id: 'caixaFiltroAr',
    nome: 'Caixa do filtro de ar',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Caixa plástica que filtra o ar de admissão, normalmente no ponto mais alto do cofre. Costuma ser a primeira peça removida para alcançar o que está embaixo.',
    acoes: ACOES_PAINEL,
  },
  {
    id: 'caixaDirecao',
    nome: 'Caixa de direção',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Cremalheira que converte o giro do volante em movimento das rodas, com o eixo intermediário subindo pelo corta-fogo até a coluna de direção.',
    atencaoOperacional:
      'É o vínculo mecânico entre o cofre e o volante: intrusão dianteira pode chegar à coluna de direção por esse caminho.',
    confirmarNaFicha: true,
    acoes: ACOES_PAINEL,
  },
  {
    id: 'semiEixos',
    nome: 'Semi-eixos',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Eixos que levam a força da transmissão às rodas motrizes, com juntas nas duas pontas.',
  },
  {
    id: 'cardan',
    nome: 'Eixo cardã',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Eixo longitudinal que leva a força ao diferencial traseiro. Só existe quando a tração não é dianteira, e corre dentro do túnel central.',
  },
  {
    id: 'diferencialTraseiro',
    nome: 'Diferencial traseiro',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Conjunto que distribui a força entre as rodas traseiras. Só existe quando a tração não é dianteira.',
  },
  {
    id: 'cubosRoda',
    nome: 'Cubos de roda',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Peça que liga a roda ao conjunto de suspensão e, nas rodas motrizes, ao semi-eixo.',
  },
  {
    id: 'escapamento',
    nome: 'Escapamento',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Linha que conduz os gases do motor até a traseira, correndo por dentro do túnel central e sob o assoalho.',
    atencaoOperacional:
      'Chega quente ao ponto onde se corta o assoalho, e permanece quente bem depois de o motor ser desligado.',
    acoes: ACOES_PAINEL,
  },
  {
    id: 'escapamento.coletor',
    nome: 'Coletor de escape',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Trecho preso ao motor que recolhe os gases dos cilindros. É a parte mais quente da linha.',
  },
  {
    id: 'escapamento.catalisador',
    nome: 'Catalisador',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao:
      'Conversor no trecho dianteiro do escapamento, sob o assoalho. Trabalha em temperatura alta e demora a esfriar.',
    atencaoOperacional: 'Considere a temperatura ao definir apoio de estabilização sob o assoalho.',
  },
  {
    id: 'escapamento.silencioso',
    nome: 'Silencioso',
    categoria: 'mecanica',
    grupo: 'mecanica',
    descricao: 'Câmara traseira que reduz o ruído dos gases, junto ao para-choque traseiro.',
  },

  // ---------- energia e riscos ----------
  {
    id: 'radiador',
    nome: 'Radiador',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Troca o calor do fluido de arrefecimento com o ar. Fica à frente de tudo, na boca do cofre do motor.',
    atencaoOperacional:
      'Contém fluido quente sob pressão e é dos primeiros conjuntos a se romper numa colisão frontal.',
  },
  {
    id: 'condensador',
    nome: 'Condensador do ar-condicionado',
    categoria: 'energia',
    grupo: 'energia',
    descricao: 'Trocador à frente do radiador, no circuito do ar-condicionado, com fluido sob pressão.',
  },
  {
    id: 'ventoinha',
    nome: 'Ventoinha',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Ventilador atrás do radiador. Pode acionar sozinha com o motor já desligado, enquanto houver energia disponível.',
    atencaoOperacional: 'Pode partir sem aviso: considere isso antes de aproximar mãos do cofre.',
  },
  {
    id: 'reservatorioExpansao',
    nome: 'Reservatório de expansão',
    categoria: 'energia',
    grupo: 'energia',
    descricao: 'Reservatório do fluido de arrefecimento, no cofre do motor, sujeito à pressão do sistema quente.',
  },
  {
    id: 'tanqueCombustivel',
    nome: 'Tanque de combustível',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Reservatório sob o assoalho traseiro, à frente do eixo traseiro. Posição e formato variam conforme o projeto.',
    atencaoOperacional:
      'Localize-o antes de cortar soleira, assoalho ou traseira, e antes de escolher ponto de apoio para estabilização.',
    confirmarNaFicha: true,
  },
  {
    id: 'linhaCombustivel',
    nome: 'Linha de combustível',
    categoria: 'energia',
    grupo: 'energia',
    descricao: 'Tubulação que leva o combustível do tanque ao motor, correndo sob o assoalho junto ao túnel central.',
    atencaoOperacional: 'Passa exatamente sob a região onde se costuma cortar o assoalho.',
    confirmarNaFicha: true,
  },
  {
    id: 'cilindroGnv',
    nome: 'Cilindro de GNV',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Cilindro de gás natural veicular, normalmente deitado no porta-malas. Trabalha em pressão muito alta e nem sempre é visível por fora do veículo.',
    material: 'Cilindro de parede espessa, com válvula e tubulação próprias.',
    atencaoOperacional:
      'Muda o corte da traseira por completo. Identifique a presença de gás antes de qualquer corte atrás do banco traseiro, e trate a válvula e a tubulação como parte do risco.',
    confirmarNaFicha: true,
  },
  {
    id: 'tubulacaoGnv',
    nome: 'Tubulação de GNV',
    categoria: 'energia',
    grupo: 'energia',
    descricao: 'Linha de alta pressão que liga o cilindro ao motor, correndo sob o assoalho ao lado da linha de combustível.',
    atencaoOperacional: 'Corre sob o assoalho em toda a extensão do veículo, não apenas na traseira.',
    confirmarNaFicha: true,
  },
  {
    id: 'bateriaAltaTensao',
    nome: 'Bateria de alta tensão',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Pacote de baterias de veículo híbrido ou elétrico, alojado sob o assoalho entre os eixos. Não é a bateria de 12 V e não se desconecta da mesma forma.',
    material: 'Pacote selado com estrutura própria; não deve ser aberto nem perfurado.',
    atencaoOperacional:
      'Fica exatamente sob a soleira e o assoalho. Nunca corte nessa faixa sem confirmar a presença e o procedimento de desativação na ficha de salvamento do modelo.',
    confirmarNaFicha: true,
  },
  {
    id: 'cabeamentoAltaTensao',
    nome: 'Cabeamento de alta tensão',
    categoria: 'energia',
    grupo: 'energia',
    descricao:
      'Cabos que ligam o pacote de alta tensão ao restante do sistema. A cor laranja é a convenção usada no próprio veículo para identificá-los.',
    atencaoOperacional:
      'Cabo laranja é sinal de alta tensão: não corte, não desconecte e não o use como apoio. Siga o procedimento do fabricante.',
    confirmarNaFicha: true,
  },
  {
    id: 'paraFogo',
    nome: 'Painel corta-fogo',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Painel transversal que separa o cofre do motor do habitáculo, à frente da coluna A. É por ele que passam direção, pedais e chicotes.',
    material: 'Chapa com isolamento; pode receber reforço junto à travessa inferior.',
    atencaoOperacional:
      'Recuo do corta-fogo sobre a pedaleira é sinal de intrusão: verifique aprisionamento de pés e pernas antes de movimentar o veículo.',
    confirmarNaFicha: true,
    classeAco: 'media',
  },
  {
    id: 'caixaDeAr',
    nome: 'Caixa de ar',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Calha entre a quina traseira do capô e o pé do para-brisa, onde ficam os limpadores e a tomada de ar da ventilação.',
    material: 'Conjunto de chapa sobre a travessa do corta-fogo.',
    atencaoOperacional:
      'Fica exatamente sobre a travessa do corta-fogo e junto ao pé da coluna A: considere-a ao planejar corte nessa região.',
    confirmarNaFicha: true,
    classeAco: 'estampagemProfunda',
  },
  {
    id: 'suporteSuspensaoTraseira',
    nome: 'Suporte e braços da suspensão traseira',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao: 'Representação didática da travessa, braços e fixações que ligam as rodas traseiras ao piso do carro. A construção varia conforme o veículo.',
    confirmarNaFicha: true,
    acoes: ACOES_PAINEL,
  },
  {
    id: 'subquadroDianteiro',
    nome: 'Quadro suporte do motor',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Berço sob o cofre do motor que sustenta motor, transmissão e braços da suspensão dianteira. Costuma ser aparafusado ao monobloco.',
    material: 'Estrutura própria, frequentemente aparafusada e não soldada.',
    atencaoOperacional:
      'Por ser aparafusado, comporta-se de forma diferente do monobloco no impacto e no tracionamento.',
    confirmarNaFicha: true,
    classeAco: 'media',
  },
  {
    id: 'tunelCentral',
    nome: 'Túnel central',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Elevação longitudinal no meio do assoalho, do corta-fogo ao banco traseiro. Por dentro passam escapamento e linhas; enrijece o assoalho.',
    material: 'Conformação do próprio assoalho, com reforços conforme o projeto.',
    atencaoOperacional:
      'Corte no assoalho junto ao túnel pode atingir linha de combustível ou escapamento quente.',
    confirmarNaFicha: true,
    classeAco: 'alta',
  },
  {
    id: 'travessaAssoalhoDianteira',
    nome: 'Travessa dianteira do assoalho',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Travessa transversal sob o assoalho, à frente dos bancos dianteiros. É nela que costumam se fixar os trilhos do banco.',
    material: 'Perfil de chapa soldado ao assoalho.',
    classeAco: 'alta',
    confirmarNaFicha: true,
  },
  {
    id: 'travessaAssoalhoTraseira',
    nome: 'Travessa traseira do assoalho',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Travessa transversal sob o assoalho, na altura da coluna B. Liga as duas soleiras e enrijece o piso do habitáculo.',
    material: 'Perfil de chapa soldado ao assoalho, próximo ao pé da coluna B.',
    classeAco: 'alta',
    confirmarNaFicha: true,
  },
  {
    id: 'assoalhoPortaMalas',
    nome: 'Assoalho do porta-malas',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Piso do compartimento de carga, atrás do banco traseiro. Costuma alojar o estepe e, quando há, o cilindro de gás.',
    material: 'Chapa conformada com poço do estepe.',
    atencaoOperacional:
      'Verifique o que está alojado abaixo dele antes de cortar a traseira.',
    confirmarNaFicha: true,
    classeAco: 'estampagemProfunda',
  },
  {
    id: 'painelTraseiro',
    nome: 'Painel traseiro',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Painel transversal que fecha a traseira da carroceria, abaixo da abertura do porta-malas.',
    material: 'Chapa de fechamento ligada às longarinas traseiras.',
    classeAco: 'estampagemProfunda',
    confirmarNaFicha: true,
  },
  {
    id: 'almaParaChoqueDianteiro',
    nome: 'Alma do para-choque dianteiro',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Viga transversal resistente (crash beam) ligando as duas longarinas na frente. Distribui a força do impacto frontal entre os dois lados da estrutura.',
    funcao:
      `Distribuir a carga inicial de impacto frontal uniformemente entre as longarinas do ${rotuloLadoVeiculo('esquerdo')} e do ${rotuloLadoVeiculo('direito')}.`,
    material: 'Aço de ultra-alta resistência ou liga de alumínio estrutural.',
    atencaoOperacional:
      'Ponto de grande rigidez na frente do veículo. Pode deformar e travar sobre componentes do motor ou arrefecimento.',
    confirmarNaFicha: true,
    classeAco: 'ultraAlta',
    acoes: ACOES_PADRAO,
  },
  {
    id: 'almaParaChoqueTraseiro',
    nome: 'Alma do para-choque traseiro',
    categoria: 'estrutura',
    grupo: 'estrutura',
    descricao:
      'Viga transversal de proteção traseira que une as longarinas traseiras sob a capa plástica. Protege a traseira e o compartimento de carga.',
    funcao:
      'Absorver impactos traseiros de baixa e média intensidade e proteger a integridade do vão traseiro.',
    material: 'Perfil metálico estrutural dimensionado para absorção de impacto traseiro.',
    atencaoOperacional:
      'Verifique proximidade com o tanque de combustível ou cilindro de GNV antes de cortar ou tracionar.',
    confirmarNaFicha: true,
    classeAco: 'alta',
    acoes: ACOES_PADRAO,
  },
  {
    id: 'zonaAbsorcaoDianteira',
    nome: 'Zona de absorção dianteira',
    categoria: 'zonaDeformacao',
    grupo: 'estrutura',
    descricao:
      'Região representada como programada para se deformar e absorver energia antes do habitáculo. Representação didática: a maquete não deforma a carroceria de verdade.',
    funcao:
      'Dissipar a energia cinética do impacto frontal por deformação controlada antes que a desaceleração atinja a cabine.',
  },
  {
    id: 'zonaAbsorcaoTraseira',
    nome: 'Zona de absorção traseira',
    categoria: 'zonaDeformacao',
    grupo: 'estrutura',
    descricao:
      'Equivalente traseiro da zona de absorção. Exibida apenas como volume didático.',
    funcao:
      'Absorver colisões traseiras preservando a célula de sobrevivência dos passageiros e os dutos/tanque de combustível.',
  },
  {
    id: 'capo',
    nome: 'Capô',
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao:
      'Tampa do compartimento dianteiro. Articula na quina traseira, junto ao para-brisa; abri-lo é o que dá acesso visual ao cofre do motor.',
    funcao:
      'Proteger os agregados mecânicos do cofre e deformar-se progressivamente em atropelamentos ou colisões frontais.',
    acoes: ['destacar', 'transparencia', 'abrir', 'afastar', 'ocultar', 'restaurar'],
  },
  {
    id: 'teto',
    nome: 'Teto',
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao: 'Painel de teto. Torná-lo transparente revela os reforços.',
    funcao:
      'Fechar superiormente o habitáculo e conectar aerodinamicamente a estrutura das colunas da carroceria.',
    acoes: ACOES_PAINEL,
  },
  {
    id: 'tampaTraseira',
    nome: 'Tampa traseira',
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao: 'Tampa do porta-malas. No hatch acompanha o vidro traseiro.',
    funcao:
      'Garantir acesso ao compartimento de carga traseiro e vedar o habitáculo contra intempéries.',
    acoes: ACOES_PORTA,
  },
  { id: 'paineisLateraisEsquerda', nome: `Painéis laterais do ${rotuloLadoVeiculo('esquerdo')}`, categoria: 'carroceria', grupo: 'carroceria', descricao: `Paralama dianteiro e painel traseiro do ${rotuloLadoVeiculo('esquerdo')}, com o recorte das caixas de roda.`, acoes: ACOES_PAINEL },
  { id: 'paineisLateraisDireita', nome: `Painéis laterais do ${rotuloLadoVeiculo('direito')}`, categoria: 'carroceria', grupo: 'carroceria', descricao: `Paralama dianteiro e painel traseiro do ${rotuloLadoVeiculo('direito')}, com o recorte das caixas de roda.`, acoes: ACOES_PAINEL },
  {
    id: 'paraBrisa',
    nome: 'Para-brisa',
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao: 'Vidro dianteiro, representado como superfície translúcida.',
    funcao:
      'Vidro estrutural laminado que contribui para a rigidez torsional do teto e apoia a deflagração do airbag do passageiro.',
    material: 'Laminado: camadas de vidro unidas por uma película que retém os fragmentos.',
    atencaoOperacional: 'A técnica de manejo difere da usada em vidro temperado.',
    confirmarNaFicha: true,
  },
  {
    id: 'vidroTraseiro',
    nome: 'Vidro traseiro',
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao: 'Vidro traseiro, representado como superfície translúcida.',
    funcao:
      'Fechamento traseiro em vidro temperado com desembaçador elétrico térmico integrado.',
    material: 'Em muitos veículos é temperado, mas pode ser laminado ou usar outra solução.',
    atencaoOperacional: 'Confira a marcação do vidro antes de intervir.',
    confirmarNaFicha: true,
  },
  ...LADOS.map((lado): Entrada => ({
    id: `vidroVigia${lado.sufixo}`,
    nome: `Vidro lateral fixo traseiro do ${rotuloLadoVeiculo(lado.chave)}`,
    categoria: 'carroceria',
    grupo: 'carroceria',
    descricao: 'Vigia fixa entre a janela da porta traseira e a coluna C; não acompanha a remoção da porta.',
    funcao:
      'Fechamento lateral fixo que amplia o campo de visão lateral/traseiro dos ocupantes.',
    material: 'A composição varia conforme o veículo; confirme pela marcação e pela ficha de salvamento.',
    confirmarNaFicha: true,
  })),
  { id: 'paraChoqueDianteiro', nome: 'Para-choque dianteiro', categoria: 'carroceria', grupo: 'carroceria', descricao: 'Elemento externo da extremidade dianteira.', acoes: ACOES_PAINEL },
  { id: 'paraChoqueTraseiro', nome: 'Para-choque traseiro', categoria: 'carroceria', grupo: 'carroceria', descricao: 'Elemento externo da extremidade traseira.', acoes: ACOES_PAINEL },
  { id: 'rodas', nome: 'Rodas', categoria: 'carroceria', grupo: 'carroceria', descricao: 'Conjunto de rodas, usado como referência de escala e de apoio do veículo.', acoes: ACOES_SOMENTE_DESTAQUE },
  ...entradasPorta('portaDianteiraEsquerda', `Porta dianteira do ${rotuloLadoVeiculo('esquerdo')}`),
  ...entradasPorta('portaDianteiraDireita', `Porta dianteira do ${rotuloLadoVeiculo('direito')}`),
  ...entradasPorta('portaTraseiraEsquerda', `Porta traseira do ${rotuloLadoVeiculo('esquerdo')}`),
  ...entradasPorta('portaTraseiraDireita', `Porta traseira do ${rotuloLadoVeiculo('direito')}`),
  { id: 'terceiraPortaEsquerda', nome: `Terceira porta do ${rotuloLadoVeiculo('esquerdo')}`, categoria: 'carroceria', grupo: 'carroceria', descricao: `Chapa maciça no lugar da porta traseira do ${rotuloLadoVeiculo('esquerdo')}: footprint de um veículo tratado como carroceria de duas portas, na oficina de corte.`, acoes: ACOES_PORTA },
  { id: 'terceiraPortaDireita', nome: `Terceira porta do ${rotuloLadoVeiculo('direito')}`, categoria: 'carroceria', grupo: 'carroceria', descricao: `Chapa maciça no lugar da porta traseira do ${rotuloLadoVeiculo('direito')}: footprint de um veículo tratado como carroceria de duas portas, na oficina de corte.`, acoes: ACOES_PORTA },
  ...entradasBanco('Motorista', 'motorista'),
  ...entradasBanco('Passageiro', 'passageiro dianteiro'),
  ...entradasBanco('TraseiroEsquerdo', `ocupante traseiro do ${rotuloLadoVeiculo('esquerdo')}`),
  ...entradasBanco('TraseiroCentral', 'ocupante traseiro central'),
  ...entradasBanco('TraseiroDireito', `ocupante traseiro do ${rotuloLadoVeiculo('direito')}`),
  { id: 'painel', nome: 'Painel', categoria: 'habitaculo', grupo: 'habitaculo', descricao: 'Painel de instrumentos. Torná-lo transparente revela a travessa estrutural atrás dele.', acoes: ACOES_PAINEL },
  { id: 'volante', nome: 'Volante', categoria: 'habitaculo', grupo: 'habitaculo', descricao: 'Volante e cubo de direção simplificados. Aloja o airbag frontal do motorista quando o perfil o inclui.' },
  {
    id: 'colunaDirecao',
    nome: 'Coluna de direção',
    categoria: 'habitaculo',
    grupo: 'habitaculo',
    descricao:
      'Eixo mecânico que liga o volante à cremalheira no cofre do motor, atravessando a parede corta-fogo.',
    atencaoOperacional:
      'Em impactos frontais pode recuar em direção ao motorista ou travar os pedais e membros inferiores.',
    confirmarNaFicha: true,
    acoes: ACOES_SOMENTE_DESTAQUE,
  },
  {
    id: 'pedais',
    nome: 'Conjunto de pedais',
    categoria: 'habitaculo',
    grupo: 'habitaculo',
    descricao:
      'Pedais sob o painel na pedaleira do condutor. Em colisões frontais com invasão da pedaleira, frequentemente aprisionam pés e tornozelos.',
    atencaoOperacional:
      'Avalie aprisionamento de pés e tornozelos. Pode exigir corte das hastes ou elevação do painel com cilindro extensor.',
    acoes: ACOES_SOMENTE_DESTAQUE,
  },
  { id: 'piso', nome: 'Piso do habitáculo', categoria: 'habitaculo', grupo: 'habitaculo', descricao: 'Piso simplificado do habitáculo.' },
  {
    id: 'freioDeMao',
    nome: 'Freio de mão',
    categoria: 'habitaculo',
    grupo: 'habitaculo',
    descricao:
      'Alavanca do freio de estacionamento, entre os bancos dianteiros. Seu acionamento pode ajudar na imobilização, conforme o estado do sistema. Não substitui a estabilização e não garante ausência de deslocamento.',
    acoes: ['destacar', 'abrir', 'restaurar'],
  },
  {
    id: 'bateria12v',
    nome: 'Bateria de baixa tensão',
    categoria: 'energia',
    grupo: 'energia',
    descricao: 'Fonte elétrica representada no compartimento dianteiro. A localização real pode ser dianteira, traseira ou interna.',
    material: 'Bateria automotiva de baixa tensão, representada de forma didática.',
    atencaoOperacional: 'Antes de desconectar, preserve o acesso elétrico necessário para destravar portas e abaixar vidros. Siga o procedimento do fabricante para desligamento e desconexão. Desconectar baixa tensão não torna a bateria de tração desenergizada; arquiteturas híbridas e elétricas exigem identificação e guia específicos.',
    confirmarNaFicha: true,
    acoes: ACOES_SOMENTE_DESTAQUE,
  },
  ...entradasSeguranca(),
  ...DETALHES_ANATOMIA,
]

export const CATALOGO: Readonly<Record<string, ComponenteDidatico>> = Object.freeze(
  Object.fromEntries(ENTRADAS.map((e) => [e.id, normalizar(e)])),
)

export const IDS_CATALOGO: readonly string[] = Object.keys(CATALOGO)

export function componenteDidatico(id: string): ComponenteDidatico | null {
  return CATALOGO[id] ?? null
}

export function acoesDisponiveis(id: string): AcaoPeca[] {
  return CATALOGO[id]?.acoes ?? ACOES_SOMENTE_DESTAQUE
}

export function categoriaDe(id: string): CategoriaDidatica {
  return CATALOGO[id]?.categoria ?? 'carroceria'
}

export function corDe(id: string): string {
  return CATALOGO[id]?.corSemantica ?? COR_POR_CATEGORIA.carroceria
}

/**
 * Classe de aço da peça, quando a maquete declara uma.
 *
 * Devolve indefinido para tudo que não é estrutura: vidro, banco e airbag não
 * têm classe de aço, e inventar uma faria a camada mentir.
 */
export function classeAcoDe(id: string): ClasseAco | undefined {
  return CATALOGO[id]?.classeAco ?? CATALOGO[idRaiz(id)]?.classeAco
}

export function nomeDe(id: string): string {
  return CATALOGO[id]?.nome ?? id
}

export function porGrupo(grupo: ComponenteDidatico['grupo']): ComponenteDidatico[] {
  return Object.values(CATALOGO).filter((c) => c.grupo === grupo)
}

/**
 * Hierarquia de peças.
 *
 * Os ids compostos (`portaDianteiraEsquerda.chapaExterna`) já descrevem a
 * montagem. Derivar a hierarquia do id evita manter uma segunda estrutura de
 * árvore em paralelo com a cena.
 */
export function idPai(id: string): string | null {
  const corte = id.lastIndexOf('.')
  return corte < 0 ? null : id.slice(0, corte)
}

export function idRaiz(id: string): string {
  const corte = id.indexOf('.')
  return corte < 0 ? id : id.slice(0, corte)
}

/** Caminho da raiz até a peça, para a trilha de navegação do painel. */
export function cadeiaDeIds(id: string): string[] {
  const partes = id.split('.')
  return partes.map((_, i) => partes.slice(0, i + 1).join('.'))
}

/**
 * Seleção em profundidade progressiva.
 *
 * Um clique na porta acerta sempre a chapa externa, que é a peça mais externa.
 * Sem isso o aluno nunca conseguiria selecionar a porta para abri-la. A
 * primeira seleção pega o conjunto; clicar de novo dentro dele aprofunda.
 */
export function resolverSelecao(alvo: string, selecaoAtual: string | null): string {
  const raiz = idRaiz(alvo)
  if (alvo === raiz) return alvo
  if (selecaoAtual && idRaiz(selecaoAtual) === raiz) return alvo
  return raiz
}
